/**
 * Where campaign tags (`utm_*`, `am_id`) live between visits. They are kept
 * in browser storage only for a visitor who allows analytics; without that
 * the page holds them in memory and the stored copy is removed.
 *
 * Aliases: campaign tags storage, utm storage, tracking_parameters
 */

import {
  type TrackingParameters,
  trackingParametersSchema,
} from "~/lib/shared/tracking-parameters";

export const TRACKING_PARAMETERS_STORAGE_KEY = "tracking_parameters";

/** The saved tags, or none when nothing valid is stored. */
export function readStoredTrackingParameters(): TrackingParameters {
  try {
    const serialized = window.localStorage.getItem(
      TRACKING_PARAMETERS_STORAGE_KEY,
    );
    if (!serialized) return {};
    return trackingParametersSchema.parse(JSON.parse(serialized));
  } catch {
    return {};
  }
}

/**
 * Saves the tags when the visitor allows analytics, and removes the stored
 * value when they do not. Storage that throws (blocked cookies) is ignored: the
 * tags stay in memory.
 */
export function syncStoredTrackingParameters(
  parameters: TrackingParameters,
  analyticsAllowed: boolean,
): void {
  try {
    if (!analyticsAllowed) {
      window.localStorage.removeItem(TRACKING_PARAMETERS_STORAGE_KEY);
    } else if (Object.keys(parameters).length > 0) {
      window.localStorage.setItem(
        TRACKING_PARAMETERS_STORAGE_KEY,
        JSON.stringify(parameters),
      );
    }
  } catch {
    // Blocked storage: the tags stay in memory for this page load.
  }
}
